/**
 * Google Ads results that arrive on their own.
 *
 * A small script runs inside the Google Ads account once a week and sends the numbers to the CMS with a password the CMS
 * made. This file holds the pure parts: what the script may send, how each row is checked and turned into a result row,
 * and the script text itself. Nothing here talks to Google or to the database.
 *
 * Rules kept from the rest of Ads: an unknown number stays null (never 0), a row for a product we cannot name is skipped,
 * and a row that cannot be read is reported, not guessed.
 */
import { z } from "zod";
import { adResultKey, isValidIsoDate } from "./ads.ts";
import type { AdProductId } from "./adsProducts.ts";

export const GOOGLE_INGEST_LIMITS = { maxWeeks: 400, maxItems: 400, maxBodyBytes: 600_000, maxSkippedShown: 20 } as const;

const count = z.number().int().min(0).max(2_000_000_000).nullable().default(null);
const money = z.number().min(0).max(1_000_000_000).nullable().default(null);
const day = z.string().refine(isValidIsoDate, "Use a real date written YYYY-MM-DD");
const name = (max: number) => z.string().trim().min(1).max(max);

export const googleWeekRowSchema = z.object({
  /** The Monday the week starts on. */
  weekStart: day,
  campaign: name(120),
  /** Which ad this is, for example "Exact: AI interview · 4821". */
  adName: z.string().trim().max(160).nullable().default(null),
  impressions: count,
  clicks: count,
  conversions: count,
  spend: money,
});
export type GoogleWeekRow = z.infer<typeof googleWeekRowSchema>;

export const googleItemRowSchema = z.object({
  kind: z.enum(["keyword", "ad"]),
  campaign: name(120),
  name: name(160),
  /** The first day it showed. Null when Google reported none. */
  startDate: day.nullable().default(null),
  impressions: count,
  clicks: count,
  conversions: count,
  spend: money,
});
export type GoogleItemInput = z.infer<typeof googleItemRowSchema>;

export const googleIngestBodySchema = z.object({
  /** The account's currency, three letters. */
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a three-letter currency code like USD"),
  /** Rows are checked one by one in `normalizeGoogleIngest`, so one bad row does not lose the rest. */
  weeks: z.array(z.unknown()).max(GOOGLE_INGEST_LIMITS.maxWeeks).default([]),
  items: z.array(z.unknown()).max(GOOGLE_INGEST_LIMITS.maxItems).default([]),
});
export type GoogleIngestBody = z.infer<typeof googleIngestBodySchema>;

/** "SonicHire | Search | US+CA" and "Raya | Search | US+CA" are ours. Anything else is not, and is skipped. */
export function productFromCampaign(campaign: string): AdProductId | null {
  const first = campaign.split("|")[0]?.trim().toLowerCase() ?? "";
  if (first === "sonic hire" || first === "sonichire") return "sonic_hire";
  if (first === "raya") return "raya";
  return null;
}

/** The Sunday that closes a week starting on `weekStart`. */
export function weekEnd(weekStart: string): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
}

export interface GoogleResultRow {
  product: AdProductId;
  periodStart: string;
  periodEnd: string;
  campaign: string;
  adName: string | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  spend: number | null;
  currency: string;
  rowKey: string;
}

export interface GoogleItemRowOut {
  product: AdProductId;
  kind: "keyword" | "ad";
  name: string;
  nameKey: string;
  startDate: string | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  spend: number | null;
  currency: string;
}

export interface SkippedRow {
  list: "weeks" | "items";
  index: number;
  reason: string;
}

export const itemNameKey = (n: string) => n.trim().replace(/\s+/g, " ").toLowerCase();

/** Checks every row. Good rows are returned, bad ones are listed with a plain reason. */
export function normalizeGoogleIngest(body: GoogleIngestBody): { weeks: GoogleResultRow[]; items: GoogleItemRowOut[]; skipped: SkippedRow[] } {
  const weeks: GoogleResultRow[] = [];
  const items: GoogleItemRowOut[] = [];
  const skipped: SkippedRow[] = [];
  const seenWeek = new Set<string>();
  const seenItem = new Set<string>();

  body.weeks.forEach((raw, index) => {
    const p = googleWeekRowSchema.safeParse(raw);
    if (!p.success) return void skipped.push({ list: "weeks", index, reason: p.error.issues[0]?.message ?? "Could not read this row" });
    const r = p.data;
    const product = productFromCampaign(r.campaign);
    if (!product) return void skipped.push({ list: "weeks", index, reason: `“${r.campaign}” is not a SonicHire or Raya campaign` });
    if (r.impressions !== null && r.clicks !== null && r.clicks > r.impressions) return void skipped.push({ list: "weeks", index, reason: "Clicks are more than views" });
    const periodEnd = weekEnd(r.weekStart);
    const rowKey = adResultKey({ product, channel: "google_search", periodStart: r.weekStart, periodEnd, campaign: r.campaign, adName: r.adName });
    if (seenWeek.has(rowKey)) return void skipped.push({ list: "weeks", index, reason: "The same ad and week was sent twice" });
    seenWeek.add(rowKey);
    weeks.push({ product, periodStart: r.weekStart, periodEnd, campaign: r.campaign, adName: r.adName, impressions: r.impressions, clicks: r.clicks, conversions: r.conversions, spend: r.spend, currency: body.currency, rowKey });
  });

  body.items.forEach((raw, index) => {
    const p = googleItemRowSchema.safeParse(raw);
    if (!p.success) return void skipped.push({ list: "items", index, reason: p.error.issues[0]?.message ?? "Could not read this row" });
    const r = p.data;
    const product = productFromCampaign(r.campaign);
    if (!product) return void skipped.push({ list: "items", index, reason: `“${r.campaign}” is not a SonicHire or Raya campaign` });
    if (r.impressions !== null && r.clicks !== null && r.clicks > r.impressions) return void skipped.push({ list: "items", index, reason: "Clicks are more than views" });
    const nameKey = itemNameKey(r.name);
    const k = `${product}|${r.kind}|${nameKey}`;
    if (seenItem.has(k)) return void skipped.push({ list: "items", index, reason: "The same keyword or ad was sent twice" });
    seenItem.add(k);
    items.push({ product, kind: r.kind, name: r.name, nameKey, startDate: r.startDate, impressions: r.impressions, clicks: r.clicks, conversions: r.conversions, spend: r.spend, currency: body.currency });
  });

  return { weeks, items, skipped };
}

/** Days from a start date to `today` (both YYYY-MM-DD). Null when there is no start date. */
export function daysSince(startDate: string | null, today: string): number | null {
  if (!startDate || !isValidIsoDate(startDate) || !isValidIsoDate(today)) return null;
  const ms = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`);
  return ms < 0 ? null : Math.floor(ms / 86_400_000);
}

// --------------------------------------------------------------------------------------------------------------------
// The script a manager pastes into Google Ads (Tools > Scripts). It only reads; it can't change the account.
// --------------------------------------------------------------------------------------------------------------------

export function googleAdsScript(endpoint: string, token: string): string {
  const q = (s: string) => JSON.stringify(s);
  return `// DemandHub CMS: sends last week's results here. It only reads your account. It cannot change anything.
// Schedule it weekly: Tools > Scripts > this script > Frequency > Weekly, Monday.
var ENDPOINT = ${q(endpoint)};
var TOKEN = ${q(token)};

function main() {
  var currency = AdsApp.currentAccount().getCurrencyCode();
  var weeks = [];
  var items = [];

  var adRows = AdsApp.search(
    "SELECT campaign.name, ad_group.name, ad_group_ad.ad.id, segments.week, metrics.impressions, metrics.clicks, metrics.conversions, metrics.cost_micros " +
    "FROM ad_group_ad WHERE segments.date DURING LAST_30_DAYS AND campaign.status != 'REMOVED'"
  );
  while (adRows.hasNext()) {
    var a = adRows.next();
    weeks.push({
      weekStart: a.segments.week,
      campaign: a.campaign.name,
      adName: a.adGroup.name + " · " + a.adGroupAd.ad.id,
      impressions: Number(a.metrics.impressions),
      clicks: Number(a.metrics.clicks),
      conversions: Math.round(Number(a.metrics.conversions)),
      spend: Number(a.metrics.costMicros) / 1000000
    });
  }

  var kwRows = AdsApp.search(
    "SELECT campaign.name, ad_group_criterion.keyword.text, segments.date, metrics.impressions, metrics.clicks, metrics.conversions, metrics.cost_micros " +
    "FROM keyword_view WHERE segments.date DURING LAST_90_DAYS AND campaign.status != 'REMOVED'"
  );
  var byKeyword = {};
  while (kwRows.hasNext()) {
    var k = kwRows.next();
    var id = k.campaign.name + "||" + k.adGroupCriterion.keyword.text;
    var cur = byKeyword[id] || { kind: "keyword", campaign: k.campaign.name, name: k.adGroupCriterion.keyword.text, startDate: null, impressions: 0, clicks: 0, conversions: 0, spend: 0 };
    var imp = Number(k.metrics.impressions);
    if (imp > 0 && (cur.startDate === null || k.segments.date < cur.startDate)) cur.startDate = k.segments.date;
    cur.impressions += imp;
    cur.clicks += Number(k.metrics.clicks);
    cur.conversions += Number(k.metrics.conversions);
    cur.spend += Number(k.metrics.costMicros) / 1000000;
    byKeyword[id] = cur;
  }
  for (var key in byKeyword) {
    var it = byKeyword[key];
    it.conversions = Math.round(it.conversions);
    items.push(it);
  }

  var res = UrlFetchApp.fetch(ENDPOINT, {
    method: "post",
    contentType: "application/json",
    headers: { Authorization: "Bearer " + TOKEN },
    payload: JSON.stringify({ currency: currency, weeks: weeks, items: items }),
    muteHttpExceptions: true
  });
  var code = res.getResponseCode();
  Logger.log("DemandHub answered " + code + ": " + res.getContentText());
  if (code < 200 || code >= 300) throw new Error("DemandHub did not accept the results (" + code + ").");
}
`;
}

// --------------------------------------------------------------------------------------------------------------------
// What the admin sees
// --------------------------------------------------------------------------------------------------------------------

export const googleConnectionSchema = z.object({
  connected: z.boolean(),
  createdAt: z.string().nullable(),
  /** The last time the script reached us. Null when it never has. */
  lastUsedAt: z.string().nullable(),
  /** Where the script sends results. */
  endpoint: z.string(),
});
export type GoogleConnection = z.infer<typeof googleConnectionSchema>;

/** Shown once, right after a password is made. It is never returned again. */
export const googleConnectionCreatedSchema = z.object({ token: z.string(), endpoint: z.string(), script: z.string() });
export type GoogleConnectionCreated = z.infer<typeof googleConnectionCreatedSchema>;

export const googleIngestResultSchema = z.object({
  weeks: z.number().int(),
  items: z.number().int(),
  skippedCount: z.number().int(),
  skipped: z.array(z.object({ list: z.enum(["weeks", "items"]), index: z.number().int(), reason: z.string() })),
});
export type GoogleIngestResult = z.infer<typeof googleIngestResultSchema>;

/** Keywords and ads as Google reports them (running totals since they started). A figure Google did not send is null: "Not available", never 0. */
export const googleItemSchema = z.object({
  id: z.string(),
  product: z.enum(["sonic_hire", "raya"]),
  kind: z.enum(["keyword", "ad"]),
  name: z.string(),
  startDate: z.string().nullable(),
  impressions: z.number().nullable(),
  clicks: z.number().nullable(),
  conversions: z.number().nullable(),
  spend: z.number().nullable(),
  currency: z.string().nullable(),
  updatedAt: z.string(),
});
export type GoogleItem = z.infer<typeof googleItemSchema>;
export const googleItemsResponseSchema = z.object({ items: z.array(googleItemSchema) });
